import * as React from 'react'
import { Row, Col } from 'reactstrap'
import { AccentCheckUnbound } from '../../unbound/AccentCheckUnbound';
import { AccentTextUnbound } from '../../unbound/AccentTextUnBound';
import { AccentComboUnbound, AccentMultiComboUnbound } from '../../unbound/AccentComboUnbound';
import { AccentDateUnbound } from '../../unbound/AccentDateUnbound';
import { AccentDateTimeUnbound } from '../../unbound/AccentDateTimeUnbound';
import { AccentNumberUnbound } from '../../unbound/AccentNumberUnbound';
import { AccentSpinner } from '../../AccentSpinner';
import { queryNoTracking } from '../../../services/DataService';

const query_CustomDocument_ParamList = queryNoTracking("CustomDocument_ParamList");

const normalizeMultiValue = (value) => {
    if (Array.isArray(value)) {
        return value.filter(v => v !== undefined && v !== null);
    }

    if (value === undefined || value === null || value === '') {
        return [];
    }

    return [value];
};

const hasLookupItems = (param) => Array.isArray(param?.CustomDocumentParamaterListItems) && param.CustomDocumentParamaterListItems.length > 0;
const isMultiLookupParam = (param) => !!param?.MultiValue && hasLookupItems(param);

export const CustomParamsCtrl = ({
    customDocumentID,
    customDocumentVersionID,    
    defaultCustomParamValues,
    onCustomParamsChange
}) => {
    const [params, setParams] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [paramValues, setParamValues] = React.useState({});

    React.useEffect(() => {
        if (!customDocumentID) {
            setParams([]);
            setParamValues({});
            return;
        }

        setLoading(true);
        query_CustomDocument_ParamList.getAllWithCache({ 
            customDocumentID: customDocumentID, 
            customDocumentVersionID: customDocumentVersionID 
        }).then(r => {
            if (r && Array.isArray(r)) {
                // Sort alphabetically by Name
                const sortedParams = [...r].sort((a, b) => {
                    const nameA = (a.Name || '').toLowerCase();
                    const nameB = (b.Name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                
                setParams(sortedParams);
                
                // Initialize param values from defaultCustomParamValues or DefaultValue
                const initialValues = {};
                sortedParams.forEach(param => {
                    const paramName = param.Name;
                    const hasExternalValue = defaultCustomParamValues && Object.prototype.hasOwnProperty.call(defaultCustomParamValues, paramName);

                    if (hasExternalValue) {
                        initialValues[paramName] = isMultiLookupParam(param)
                            ? normalizeMultiValue(defaultCustomParamValues[paramName])
                            : defaultCustomParamValues[paramName];
                    } else if (param.DefaultValue !== undefined && param.DefaultValue !== null) {
                        initialValues[paramName] = isMultiLookupParam(param)
                            ? normalizeMultiValue(param.DefaultValue)
                            : param.DefaultValue;
                    } else if (isMultiLookupParam(param)) {
                        // Required for multi-value DevExpress params when no selection is made.
                        initialValues[paramName] = [];
                    }
                });
                
                setParamValues(initialValues);
                
                // Notify parent of initial values
                if (onCustomParamsChange && Object.keys(initialValues).length > 0) {
                    onCustomParamsChange(initialValues);
                }
            } else {
                setParams([]);
                setParamValues({});
            }
        }).catch(() => {
            // Silently return null on error
            setParams([]);
            setParamValues({});
        }).finally(() => {
            setLoading(false);
        });
    }, [customDocumentID, customDocumentVersionID]);

    const handleParamChange = (paramName, value) => {
        const newValues = {
            ...paramValues,
            [paramName]: value
        };
        setParamValues(newValues);
        
        if (onCustomParamsChange) {
            onCustomParamsChange(newValues);
        }
    };

    const renderParamControl = (param) => {
        const paramName = param.Name;
        const paramDesc = param.Description;
        const paramType = param.Type || '';
        const defaultValue = paramValues[paramName] !== undefined 
            ? paramValues[paramName] 
            : (param.DefaultValue !== undefined && param.DefaultValue !== null ? param.DefaultValue : null);
        
        // Determine column width based on parameter type
        let colWidth = 3; // Default md={3}
        if (paramType === 'Boolean') {
            colWidth = 3;
        } else if (param.CustomDocumentParamaterListItems && param.CustomDocumentParamaterListItems.length > 0) {
            colWidth = 4;
        } else {
            colWidth = 4;
        }

        // Render dropdown if list items exist
        if (hasLookupItems(param)) {
            const dropdownData = param.CustomDocumentParamaterListItems.map(item => ({
                ID: item.ID,
                Description: item.Description
            }));

            if (isMultiLookupParam(param)) {
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentMultiComboUnbound
                            data={dropdownData}
                            valueField="ID"
                            displayField="Description"
                            defaultValue={normalizeMultiValue(defaultValue)}
                            label={paramDesc}
                            checkedListMode={true}
                            commitOnClose={true}
                            showSelectedCheckIcon={true}
                            onChange={(value) => handleParamChange(paramName, value || [])}
                        />
                    </Col>
                );
            }

            return (
                <Col key={paramName} md={colWidth}>
                    <AccentComboUnbound
                        data={dropdownData}
                        valueField="ID"
                        displayField="Description"
                        defaultValue={defaultValue}
                        label={paramDesc}
                        onChange={(value) => handleParamChange(paramName, value)}
                    />
                </Col>
            );
        }

        // Render based on parameter type
        switch (paramType) {
            case 'Boolean':
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentCheckUnbound
                            tagName={`customParam_${paramName}`}
                            defaultValue={defaultValue || false}
                            onChange={(e) => handleParamChange(paramName, e.checked)}
                            label={paramDesc}
                        />
                    </Col>
                );

            case 'String':
            case 'Guid':
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentTextUnbound
                            defaultValue={defaultValue}
                            label={paramDesc}
                            onChange={(value) => handleParamChange(paramName, value)}
                        />
                    </Col>
                );

            case 'Date':
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentDateUnbound
                            defaultValue={defaultValue}
                            label={paramDesc}
                            onChange={(value) => handleParamChange(paramName, value)}
                        />
                    </Col>
                );

            case 'DateTime':
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentDateTimeUnbound
                            defaultValue={defaultValue}
                            label={paramDesc}
                            onChange={(value) => handleParamChange(paramName, value)}
                        />
                    </Col>
                );

            case 'Time':
                // Use text input for time since there's no dedicated TimeUnbound component
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentTextUnbound
                            defaultValue={defaultValue}
                            label={paramDesc}
                            onChange={(value) => handleParamChange(paramName, value)}
                        />
                    </Col>
                );

            case 'Int16':
            case 'Int32':
            case 'Int64':
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentNumberUnbound
                            defaultValue={defaultValue}
                            label={paramDesc}
                            isInt={true}
                            onChange={(value) => handleParamChange(paramName, value)}
                        />
                    </Col>
                );

            case 'Float':
            case 'Double':
            case 'Decimal':
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentNumberUnbound
                            defaultValue={defaultValue}
                            label={paramDesc}
                            isInt={false}
                            onChange={(value) => handleParamChange(paramName, value)}
                        />
                    </Col>
                );

            default:
                // Fallback to text input for unknown types
                return (
                    <Col key={paramName} md={colWidth}>
                        <AccentTextUnbound
                            defaultValue={defaultValue}
                            label={paramDesc}
                            onChange={(value) => handleParamChange(paramName, value)}
                        />
                    </Col>
                );
        }
    };

    if (!customDocumentID) {
        return null;
    }

    if (loading) {
        return (
            <Row>
                <Col md={12}>
                    <AccentSpinner />
                </Col>
            </Row>
        );
    }

    if (!params || params.length === 0) {
        return null;
    }

    return (
        <Row>
            {params.map(param => renderParamControl(param))}
        </Row>
    );
};
